import React, { useEffect, useState } from "react";
import { MessageCircleQuestion, Send, Loader2, EyeOff, Clock, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import QuestionReplies from "@/components/QuestionReplies";
import { Link } from "react-router-dom";

const REPLY_MODES = [
  { value: "alexia", title: "Să-mi răspundă doar Alexia", text: "Răspunsul vine de la mine, public, sub întrebare." },
  { value: "anyone", title: "Poate răspunde oricine citește blogul", text: "Cititorii pot lăsa și ei răspunsuri; eu pot interveni oricând." },
];
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import CoffeeBackground from "@/components/CoffeeBackground";

const inputCls =
  "w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-4 py-3 text-sm outline-none focus:border-[var(--teal)] focus:ring-2 focus:ring-[var(--tint-mint)]";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" }) : "");

const InlineAnswer = ({ q, onSaved }) => {
  const [answer, setAnswer] = useState(q.answer || "");
  const [open, setOpen] = useState(!q.answer);
  const [busy, setBusy] = useState("");

  const draft = async () => {
    setBusy("draft");
    try {
      const { data } = await api.post(`/admin/questions/${q.id}/draft`);
      setAnswer(data.draft);
    } catch (e) {
      toast.error(formatApiError(e, "Asistentul n-a putut schița"));
    } finally {
      setBusy("");
    }
  };

  const publish = async () => {
    if (answer.trim().length < 10) {
      toast.error("Scrie un răspuns înainte să publici.");
      return;
    }
    setBusy("publish");
    try {
      await api.patch(`/admin/questions/${q.id}`, { answer, status: "publicată" });
      onSaved({ ...q, answer, answered_at: new Date().toISOString() });
      setOpen(false);
      toast.success("Răspuns publicat.");
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut publica"));
    } finally {
      setBusy("");
    }
  };

  const hide = async () => {
    setBusy("hide");
    try {
      await api.patch(`/admin/questions/${q.id}`, { status: "respinsă" });
      onSaved(null);
      toast.success("Întrebarea a fost ascunsă de pe blog.");
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut ascunde"));
    } finally {
      setBusy("");
    }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid={`qa-edit-${q.id}`} className="mt-4 text-[11px] uppercase tracking-[0.2em] text-[var(--teal-dark)] underline decoration-dotted">
        Modifică răspunsul
      </button>
    );
  }
  return (
    <div data-testid={`qa-inline-answer-${q.id}`} className="mt-5 rounded-2xl bg-[var(--tint-mint)] p-4">
      <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--teal-dark)]">Doar tu vezi asta · răspunde direct de aici</p>
      <textarea
        data-testid={`qa-answer-input-${q.id}`}
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        rows={6}
        maxLength={8000}
        placeholder="Răspunsul tău, în vocea ta. Paragrafele se separă printr-un rând gol."
        className={`mt-3 ${inputCls} leading-relaxed`}
      />
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={draft} disabled={!!busy} data-testid={`qa-draft-${q.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-[color-mix(in_srgb,var(--teal)_40%,transparent)] px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--teal-dark)] disabled:opacity-50">
          {busy === "draft" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" strokeWidth={1.6} />} Schiță AI
        </button>
        <button type="button" onClick={publish} disabled={!!busy} data-testid={`qa-publish-${q.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--teal)] px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-white disabled:opacity-50">
          {busy === "publish" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" strokeWidth={1.8} />} Publică răspunsul
        </button>
        {q.answer && (
          <button type="button" onClick={() => setOpen(false)} className="rounded-full px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#F7F1E8]/68">
            Renunță
          </button>
        )}
        <button type="button" onClick={hide} disabled={!!busy} data-testid={`qa-hide-${q.id}`} className="ml-auto inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#F7F1E8]/68 hover:bg-[var(--tint-butter)] disabled:opacity-50">
          <EyeOff className="h-3.5 w-3.5" strokeWidth={1.8} /> Ascunde de pe blog
        </button>
      </div>
    </div>
  );
};

const AnswerCard = ({ q, index, isOwner, onSaved }) => (
  <article id={`q-${q.id}`} data-testid={`qa-item-${index}`} className="scroll-mt-28 rounded-3xl bg-[#2B1B13] p-7 ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)] sm:p-9">
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] uppercase tracking-[0.25em] text-[var(--teal-dark)]">
      <span>{q.nickname ? `întrebare de la „${q.nickname}”` : "întrebare anonimă"} · {fmt(q.created_at)}</span>
      <span data-testid={`qa-mode-${index}`} className={`rounded-full px-2 py-0.5 text-[10px] ${q.reply_mode === "anyone" ? "bg-[var(--tint-butter)] text-[var(--sun-dark)]" : "bg-[var(--tint-mint)] text-[var(--teal-dark)]"}`}>
        {q.reply_mode === "anyone" ? "răspunde oricine" : "răspunde doar Alexia"}
      </span>
    </p>
    <h3 className="mt-3 font-serif text-2xl font-light leading-snug text-[#F7F1E8]">„{q.question}”</h3>
    {q.answer ? (
      <div className="mt-5 border-l-2 border-[var(--teal)] pl-5">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#F7F1E8]/68">Răspunsul meu · {fmt(q.answered_at)}</p>
        {q.answer.split(/\n{2,}/).map((par, i) => (
          <p key={i} className="mt-3 text-base leading-relaxed text-[#F7F1E8]/90">{par}</p>
        ))}
      </div>
    ) : (
      <p data-testid={`qa-pending-${index}`} className="mt-4 inline-flex items-center gap-2 text-sm italic text-[#F7F1E8]/68">
        <Clock className="h-3.5 w-3.5" strokeWidth={1.6} /> Încă nu am răspuns — dar am citit-o.
      </p>
    )}
    {isOwner && <InlineAnswer q={q} onSaved={onSaved} />}
    {q.reply_mode === "anyone" && <QuestionReplies q={q} isOwner={isOwner} onChange={onSaved} />}
  </article>
);

export const QuestionsPage = () => {
  const [question, setQuestion] = useState("");
  const [nickname, setNickname] = useState("");
  const [replyMode, setReplyMode] = useState("alexia");
  const [notifyEmail, setNotifyEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [answered, setAnswered] = useState([]);
  const { user } = useAuth();

  const loadQuestions = () => api.get("/questions").then((r) => setAnswered(r.data)).catch(() => {});
  useEffect(() => {
    loadQuestions();
  }, []);

  const onSaved = (nq, id) => setAnswered((l) => (nq ? l.map((x) => (x.id === nq.id ? nq : x)) : l.filter((x) => x.id !== id)));

  const submit = async (e) => {
    e.preventDefault();
    if (question.trim().length < 10) {
      toast.error("Scrie întrebarea în câteva cuvinte mai mult, ca să o înțeleg bine.");
      return;
    }
    setSending(true);
    try {
      await api.post("/questions", { question: question.trim(), nickname: nickname.trim(), reply_mode: replyMode, notify_email: notifyEmail.trim() || null });
      setSent(true);
      loadQuestions();
    } catch (err) {
      toast.error(formatApiError(err, "Nu am putut trimite întrebarea"));
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="questions-page" className="relative overflow-hidden">
      <Seo
        title="Cutia cu întrebări anonime"
        description="Pune-i Alexiei o întrebare fără să-ți spui numele — despre relații, oameni, viața de adult. Răspunsurile apar aici, public, pentru toți cei care se regăsesc."
      />
      <CoffeeBackground slot="questions" testId="questions-bg" />
      <div className="relative mx-auto max-w-5xl px-6 py-20">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#2B1B13]/80 px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-[var(--teal-dark)]">
            <MessageCircleQuestion className="h-3.5 w-3.5" strokeWidth={1.6} /> 100% anonim
          </div>
          <h1 data-testid="questions-title" className="mt-6 font-serif text-5xl font-light leading-tight text-[#F7F1E8] sm:text-6xl">
            Cutia cu <span className="italic text-[var(--teal)]">întrebări anonime</span>
          </h1>
          <p className="mt-6 text-lg font-light leading-relaxed text-[#F7F1E8]/86">
            Sunt întrebări pe care nu le pui nimănui cu numele tău lângă ele. Aici le poți pune fără să-ți spui numele — nu-ți cer
            e-mail, nu văd cine ești. Întrebarea ta apare mai jos, iar eu răspund public, pe rând, pentru că bănuiesc că nu ești
            singura persoană care se întreabă asta.
          </p>
          <p className="mt-4 inline-flex items-center gap-2 text-sm text-[#F7F1E8]/72">
            <EyeOff className="h-3.5 w-3.5" strokeWidth={1.6} /> Nu salvăm nume, e-mail sau alte date despre tine.
          </p>
        </div>

        <div className="mt-12 max-w-2xl">
          {sent ? (
            <div data-testid="question-sent" className="rounded-3xl bg-[#2B1B13] p-8 ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)]">
              <p className="text-xs uppercase tracking-[0.3em] text-[var(--teal-dark)]">A ajuns în cutie</p>
              <h2 className="mt-3 font-serif text-3xl font-light text-[#F7F1E8]">Mulțumesc pentru încredere.</h2>
              <p className="mt-4 text-base leading-relaxed text-[#F7F1E8]/86">
                Întrebarea ta e deja mai jos, în cutie. Răspunsul meu apare sub ea în zilele următoare{notifyEmail.trim() ? " — și îți trimit un e-mail când e gata" : " — revino să-l citești"}.
              </p>
              <button type="button" onClick={() => { setSent(false); setQuestion(""); setNickname(""); setReplyMode("alexia"); setNotifyEmail(""); }} data-testid="question-another" className="mt-6 text-xs uppercase tracking-[0.2em] text-[var(--teal-dark)] underline decoration-dotted">
                Mai pun una
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="question-form" className="space-y-4 rounded-3xl bg-[#2B1B13] p-8 ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)]">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Întrebarea ta</label>
                <textarea data-testid="question-text" value={question} onChange={(e) => setQuestion(e.target.value)} rows={5} maxLength={2000} placeholder="Ce ai vrea să mă întrebi, dacă nimeni n-ar ști că tu ai întrebat?" className={`mt-2 ${inputCls} leading-relaxed`} required />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Cine ai vrea să-ți răspundă?</p>
                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="reply-mode-options">
                  {REPLY_MODES.map((m) => (
                    <label key={m.value} data-testid={`reply-mode-${m.value}`} className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 transition-colors ${replyMode === m.value ? "border-[var(--teal)] bg-[var(--tint-mint)]" : "border-[color-mix(in_srgb,var(--milk)_18%,transparent)] hover:bg-[#0D0907]"}`}>
                      <input type="radio" name="reply_mode" value={m.value} checked={replyMode === m.value} onChange={() => setReplyMode(m.value)} className="mt-1 accent-[var(--teal)]" />
                      <span>
                        <span className="block text-sm font-medium text-[#F7F1E8]">{m.title}</span>
                        <span className="mt-0.5 block text-xs text-[#F7F1E8]/78">{m.text}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Cum să-ți spun (opțional)</label>
                <input data-testid="question-nickname" value={nickname} onChange={(e) => setNickname(e.target.value)} maxLength={40} placeholder="un pseudonim, o inițială, orice — sau nimic" className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Vrei să te anunț când răspund? (opțional)</label>
                <input
                  data-testid="question-notify-email"
                  type="email"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  placeholder="un e-mail — rămâne privat, nu apare nicăieri"
                  className={`mt-2 ${inputCls}`}
                />
                <p className="mt-1 text-xs text-[#F7F1E8]/62">Îl folosesc o singură dată, pentru a-ți trimite linkul către răspuns, apoi îl șterg. Nici eu nu-l văd în Atelier.</p>
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="question-submit"
                className="inline-flex items-center gap-2 rounded-full bg-[var(--teal)] px-6 py-3.5 text-xs font-medium uppercase tracking-[0.2em] text-white transition-transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.6} /> : <Send className="h-3.5 w-3.5" strokeWidth={1.6} />}
                {sending ? "Se trimite…" : "Pune întrebarea anonim"}
              </button>
            </form>
          )}
        </div>

        <section className="mt-20" data-testid="qa-list">
          <p className="text-xs uppercase tracking-[0.3em] text-[#F7F1E8]/68">Întrebările din cutie</p>
          {answered.length === 0 ? (
            <p data-testid="qa-empty" className="mt-4 max-w-xl font-serif text-2xl font-light text-[#F7F1E8]/86">
              Cutia e încă goală. Prima întrebare poate fi a ta.
            </p>
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-6">
              {answered.map((q, i) => <AnswerCard key={q.id} q={q} index={i} isOwner={!!user} onSaved={(nq) => onSaved(nq, q.id)} />)}
            </div>
          )}
        </section>

        <p data-testid="questions-bridge" className="mt-16 max-w-xl font-serif text-xl text-[#F7F1E8]/86">
          Ai ceva ce n-ai spune public?{" "}
          <Link to="/hai-la-o-cafea" data-testid="questions-to-talk-link" className="text-[var(--accent)] underline decoration-dotted underline-offset-4">
            Hai la o cafea
          </Link>
          {" "}— acolo vorbim doar noi doi.
        </p>
      </div>
    </main>
  );
};

export default QuestionsPage;
